/**
 * Responsibility: keep the stretch of track around the rider on the GPU, and drop the rest.
 *
 * ## Chunked along the line, not by tile
 *
 * Terrain is streamed in square tiles because terrain is two-dimensional. Track is not: it is one number
 * long. So it is chunked by *distance along the route*, which needs no spatial index, no residency disc and
 * no eviction arithmetic — a chunk is in range if its distance range overlaps the window around the rider.
 *
 * That also means the track cannot be built by the tile worker, and should not be. It is a few hundred
 * segments per chunk against a tile's two thousand, it is a pure function of the route, and keeping it out of
 * `tileContent` is what leaves the flying experience untouched by any of this.
 *
 * ## Why the geometry is world-absolute here
 *
 * Tiles store vertices relative to their own corner because a float32 buffer loses precision far from the
 * origin. A track chunk is short enough that the same trick would buy nothing measurable, and it is rebuilt
 * constantly as the rider moves, so the simpler thing is the right one — chunks are positioned at the origin
 * and hold absolute coordinates.
 */

import * as THREE from 'three';

import { buildTrack } from '../railway/trackGeometry.js';
import { hexToRgb } from '../world/palette.js';
import { RAILWAY, RAILWAY_PALETTE } from '../config.js';

/** Floats per vertex, for both attributes. */
const COMPONENTS = 3;

/** Vertices per segment. */
const VERTICES_PER_SEGMENT = 2;

/**
 * Create the track view.
 *
 * @param {THREE.Scene} scene
 * @param {object} [options]
 * @param {object} [options.tuning] - Overrides for the `RAILWAY` config block.
 * @returns {object}
 */
export function createTrackView(scene, { tuning: overrides = RAILWAY } = {}) {
  if (!scene || !scene.isScene) {
    throw new Error('createTrackView: a Three.js scene is required');
  }

  const tuning = overrides === RAILWAY ? RAILWAY : { ...RAILWAY, ...overrides };

  /*
    One material for every chunk, like the terrain view. Fog is on so the rails fade into the distance with
    the ground rather than running to the clip plane as two bright lines.
  */
  const material = new THREE.LineBasicMaterial({ vertexColors: true, fog: true, transparent: false });

  const low = hexToRgb(RAILWAY_PALETTE.LOW);
  const high = hexToRgb(RAILWAY_PALETTE.HIGH);

  /** Resident chunks by index. */
  const resident = new Map();

  /** Which route the chunks were built from. Cleared wholesale when it changes. */
  let route = null;

  let segmentTotal = 0;

  /** Convert a colour fraction into the linear space Three.js expects, via its own transform. */
  const scratch = new THREE.Color();

  function colourFor(t, out, offset) {
    scratch.setRGB(
      low.r + (high.r - low.r) * t,
      low.g + (high.g - low.g) * t,
      low.b + (high.b - low.b) * t,
      THREE.SRGBColorSpace,
    );

    out[offset] = scratch.r;
    out[offset + 1] = scratch.g;
    out[offset + 2] = scratch.b;
  }

  /** Build and mount one chunk. */
  function addChunk(index) {
    const from = index * tuning.TRACK_CHUNK;

    /*
      One station of overlap at the far end, so consecutive chunks share a joint and there is no gap in the
      rails at every chunk boundary. Drawing the shared segment twice is invisible; missing it is not.
    */
    const stations = route.surveyor
      .stationsTo(from + tuning.TRACK_CHUNK + tuning.STATION_SPACING * 2)
      .filter(
        (station) =>
          station.distance >= from && station.distance <= from + tuning.TRACK_CHUNK + tuning.STATION_SPACING,
      );

    if (stations.length < 2) return;

    const segments = buildTrack(stations, { tuning });
    if (segments.length === 0) return;

    const positions = new Float32Array(segments.length * VERTICES_PER_SEGMENT * COMPONENTS);
    const colors = new Float32Array(positions.length);

    let write = 0;
    for (const seg of segments) {
      positions[write] = seg.x1;
      positions[write + 1] = seg.y1;
      positions[write + 2] = seg.z1;
      colourFor(seg.t1, colors, write);
      write += COMPONENTS;

      positions[write] = seg.x2;
      positions[write + 1] = seg.y2;
      positions[write + 2] = seg.z2;
      colourFor(seg.t2, colors, write);
      write += COMPONENTS;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, COMPONENTS));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, COMPONENTS));
    geometry.computeBoundingSphere();

    const mesh = new THREE.LineSegments(geometry, material);
    mesh.frustumCulled = true;

    // Nothing about a chunk ever moves, so Three.js is told to stop recomposing its matrix every frame.
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();

    scene.add(mesh);
    resident.set(index, { mesh, segmentCount: segments.length });
    segmentTotal += segments.length;
  }

  function removeChunk(index, entry) {
    scene.remove(entry.mesh);
    entry.mesh.geometry.dispose();
    resident.delete(index);
    segmentTotal -= entry.segmentCount;
  }

  return {
    /**
     * Point the view at a route, discarding anything built from the previous one.
     *
     * @param {{surveyor: object}|null} nextRoute
     */
    useRoute(nextRoute) {
      for (const [index, entry] of [...resident]) removeChunk(index, entry);
      route = nextRoute;
    },

    /**
     * Bring the resident chunks in line with where the rider is on the line.
     *
     * Unbudgeted, unlike terrain. A chunk is a few hundred segments against a tile's two thousand and needs no
     * noise sampling at all — it is arithmetic over stations that have already been walked — so spreading it
     * over frames would add machinery to protect a cost that is not there.
     *
     * @param {number} distance - The rider's distance along the line, in world units.
     * @returns {{built: number, evicted: number}}
     */
    update(distance) {
      if (route === null) return { built: 0, evicted: 0 };

      const chunk = tuning.TRACK_CHUNK;
      const first = Math.max(0, Math.floor((distance - tuning.TRACK_BEHIND) / chunk));
      const last = Math.floor((distance + tuning.TRACK_AHEAD) / chunk);

      let evicted = 0;
      for (const [index, entry] of [...resident]) {
        if (index < first || index > last) {
          removeChunk(index, entry);
          evicted += 1;
        }
      }

      let built = 0;
      for (let index = first; index <= last; index += 1) {
        if (resident.has(index)) continue;
        addChunk(index);
        built += 1;
      }

      return { built, evicted };
    },

    /** Segments currently drawn. For the debug readout. */
    getSegmentCount() {
      return segmentTotal;
    },

    getChunkCount() {
      return resident.size;
    },

    /** Hide the track without discarding it, for switching to flying. */
    setVisible(visible) {
      for (const entry of resident.values()) entry.mesh.visible = visible;
    },

    dispose() {
      this.useRoute(null);
      material.dispose();
    },
  };
}
